/**
 * Header Component
 *
 * Fixed header: hides on scroll down, reappears on scroll up from anywhere,
 * and keeps conversion actions prominent with a centered phone number on
 * sm+ screens. On mobile (< 640px), collapses to logo + hamburger with a
 * dropdown containing navigation links, phone number, and CTA button.
 */

import { useEffect, useRef, useState } from 'react';
// @ts-ignore - vendored package has no type declarations
import {
  useProperty,
  useContentToken,
  EditableToken,
  useSlotGroup,
  EditableSlotGroup,
  useSlotGroupOverrides,
  useTokens,
} from '@vibesites/runtime/react';
// @ts-ignore - vendored package has no type declarations
import type {
  SlotGroupDefinition,
  ComponentContentSchema,
  ComponentDesignSchema,
} from '@vibesites/runtime';
// @ts-ignore - vendored package has no type declarations
import { defineComponentRequirements, branding, contact, urls } from '@vibesites/tokens';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { MenuIcon, CloseIcon } from '@vibesites/assets';
import { formatPhone } from '../utils/format-phone';
import { pickContrastLogo } from '../utils/contrast-logo';

/**
 * Ensures a URL has a protocol prefix so the browser treats it as an absolute
 * URL rather than a relative path.
 */
function ensureAbsoluteUrl(url: string | undefined | null): string {
  if (!url || url === '#') return url || '#';
  const trimmed = url.trim();
  if (/^https?:\/\//i.test(trimmed) || /^mailto:/i.test(trimmed) || /^tel:/i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.startsWith('#') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/**
 * Builds a full CTA URL by appending optional query params to a base URL.
 * Handles merging when the base URL already has a query string.
 */
function buildCtaUrl(baseUrl: string | null, queryParams: string | null): string {
  const url = ensureAbsoluteUrl(baseUrl);
  if (!queryParams || queryParams.trim() === '' || queryParams.trim() === '?utm_source=') {
    return url;
  }
  const params = queryParams.trim().startsWith('?') ? queryParams.trim() : `?${queryParams.trim()}`;
  if (url.includes('?')) {
    return `${url}&${params.slice(1)}`;
  }
  return `${url}${params}`;
}

const logoSlotDefinition: SlotGroupDefinition = {
  label: 'Header Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      // No defaultField — the component picks between mono white and mono
      // black at render time based on the primary background luminance.
      // suggestedFields surfaces all logo variants in the editor picker.
      suggestedFields: [
        'property_mono_white_logo',
        'property_mono_black_logo',
        'property_primary_logo',
        'property_secondary_logo',
      ],
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'property_name',
    },
  },
};

const ctaLinkSlotDefinition: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    label: {
      label: 'CTA Text',
      type: 'text',
      defaultValue: 'Visit Website',
    },
    url: {
      label: 'CTA URL',
      type: 'url',
      defaultField: 'property_website_url',
      suggestedFields: ['property_website_url', 'property_apply_now_url'],
    },
    query_params: {
      label: 'URL Query Parameters',
      type: 'text',
      description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
      defaultValue: '?utm_source=',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const navItems = [
  { label: 'Overview', href: '#overview' },
  { label: 'Floor Plans', href: '#floorplans' },
  { label: 'Amenities', href: '#amenities' },
  { label: 'Neighborhood', href: '#neighborhood' },
];

export function Header() {
  const property = useProperty();
  const slotGroupOverrides = useSlotGroupOverrides();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isHeaderHidden, setIsHeaderHidden] = useState(false);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const headerRef = useRef<HTMLElement>(null);

  const phoneToken = useContentToken('header.phone');
  // Fallback chain: editor override / custom_ppc_tracking_phone_number → property_call_tracking_phone
  const phone = (phoneToken && phoneToken.trim())
    ? phoneToken
    : (property.property_call_tracking_phone || '');
  const logo = useSlotGroup('header.logo', logoSlotDefinition);
  const ctaLink = useSlotGroup('header.cta_link', ctaLinkSlotDefinition);
  const hasPhone = Boolean(phone && phone.trim());

  // The header sits over `bg-primary`, so we default the logo to mono white
  // or mono black based on the design system's contrast text token. Reusing
  // `tokens.color.textOnPrimary` (already computed by `@vibesites/tokens`)
  // keeps the luminance decision in one place. Editor uploads / explicit
  // field mappings on `header.logo.image` win over this default.
  const tokens = useTokens();
  const smartDefaultLogo = pickContrastLogo(tokens.color.textOnPrimary, {
    monoWhite: property.property_mono_white_logo,
    monoBlack: property.property_mono_black_logo,
    fallback: property.property_primary_logo,
  });
  const resolvedLogo = logo.image || smartDefaultLogo;

  // Measure header height and set CSS variable
  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight, { passive: true });
    return () => {
      window.removeEventListener('resize', updateHeight);
      document.documentElement.style.removeProperty('--header-height');
    };
  }, []);

  // Smart show/hide on scroll with requestAnimationFrame debounce
  useEffect(() => {
    const handleScroll = () => {
      if (ticking.current) return;
      ticking.current = true;

      requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;

        if (delta > 5 && currentY > 100) {
          setIsHeaderHidden(true);
          setMobileMenuOpen(false);
        } else if (delta < -5) {
          setIsHeaderHidden(false);
        }

        lastScrollY.current = currentY;
        ticking.current = false;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Expose header visibility as CSS variable for other sticky elements
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--header-visible-height',
      isHeaderHidden ? '0px' : 'var(--header-height, 64px)'
    );
  }, [isHeaderHidden]);

  // Enable smooth scrolling site-wide
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-[1000] bg-primary shadow-md transition-transform duration-300 ease-in-out ${
          isHeaderHidden ? '-translate-y-full' : 'translate-y-0'
        }`}
        style={{ top: 'var(--offer-banner-height, 0px)' }}
      >
        {/* ── Mobile bar: logo + hamburger (< 640px) ── */}
        <div className="flex items-center justify-between px-4 py-3 sm:hidden">
          <EditableSlotGroup
            groupPath="header.logo"
            definition={logoSlotDefinition}
            override={slotGroupOverrides['header.logo']}
            style={{ flexShrink: 1, minWidth: 0, maxWidth: '250px' }}
          >
            <a href="/" className="block overflow-hidden no-underline">
              {resolvedLogo ? (
                <VibesitesImage
                  url={resolvedLogo}
                  alt={logo.alt || property.property_name || 'Logo'}
                  className="max-h-10 w-auto max-w-full object-contain"
                />
              ) : (
                <span className="font-heading text-2xl font-semibold text-primary-foreground">
                  {property.property_name || 'Property'}
                </span>
              )}
            </a>
          </EditableSlotGroup>

          <button
            className="flex border-none bg-transparent p-2 text-primary-foreground cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <span className="transition-transform duration-200">
              {mobileMenuOpen ? <CloseIcon size={24} /> : <MenuIcon size={24} />}
            </span>
          </button>
        </div>

        {/* ── Mobile dropdown menu ── */}
        <div
          className={`overflow-hidden transition-all duration-300 ease-in-out sm:hidden ${
            mobileMenuOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="flex flex-col gap-1 bg-primary px-6 py-4 shadow-lg">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="border-b border-primary-foreground/[0.08] py-3 font-body text-base font-medium text-primary-foreground no-underline transition-colors duration-200 hover:opacity-80"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            ))}

            <div className="my-2 h-px bg-primary-foreground/15" />

            {hasPhone && (
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="font-accent py-3 text-lg font-semibold text-primary-foreground no-underline"
              >
                {formatPhone(phone)}
              </a>
            )}

            <a
              href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
              target={ctaLink._target || '_blank'}
              rel="noopener noreferrer"
              className="mt-2 block rounded-md bg-secondary px-6 py-3.5 text-center font-body text-base font-semibold text-secondary-foreground no-underline transition-all duration-200 hover:shadow-lg"
              onClick={() => setMobileMenuOpen(false)}
            >
              {ctaLink.label || 'Visit Website'}
            </a>
          </div>
        </div>

        {/* ── Desktop / tablet bar: 3-column grid (≥ 640px) ── */}
        <div className="mx-auto hidden max-w-[1400px] sm:grid sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] sm:gap-6 sm:px-6 sm:py-4 lg:py-5">
          <div className="flex min-w-0 w-full items-center justify-start overflow-hidden">
            <EditableSlotGroup
              groupPath="header.logo"
              definition={logoSlotDefinition}
              override={slotGroupOverrides['header.logo']}
              style={{ flexShrink: 1, minWidth: 0, maxWidth: '400px' }}
            >
              <a href="/" className="block overflow-hidden no-underline">
                {resolvedLogo ? (
                  <VibesitesImage
                    url={resolvedLogo}
                    alt={logo.alt || property.property_name || 'Logo'}
                    className="max-h-10 w-auto max-w-full object-contain md:max-h-[50px] lg:max-h-[60px]"
                  />
                ) : (
                  <span className="font-heading text-2xl font-semibold text-primary-foreground truncate block">
                    {property.property_name || 'Property'}
                  </span>
                )}
              </a>
            </EditableSlotGroup>
          </div>

          <EditableToken
            path="header.phone"
            type="phone"
            label="Phone Number"
            as="div"
            className="flex items-center justify-center"
          >
            {hasPhone ? (
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="font-accent text-center text-lg font-bold text-primary-foreground no-underline transition-opacity duration-200 hover:opacity-80 md:text-xl lg:text-2xl"
              >
                {formatPhone(phone)}
              </a>
            ) : (
              <span className="font-accent text-center text-lg font-bold text-primary-foreground md:text-xl lg:text-2xl" />
            )}
          </EditableToken>

          <div className="flex w-full items-center justify-end">
            <EditableSlotGroup
              groupPath="header.cta_link"
              definition={ctaLinkSlotDefinition}
              override={slotGroupOverrides['header.cta_link']}
              style={{ display: 'inline-block' }}
            >
              <a
                href={buildCtaUrl(ctaLink.url, ctaLink.query_params)}
                target={ctaLink._target || '_blank'}
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md bg-secondary px-6 py-3 font-body text-sm font-semibold text-secondary-foreground no-underline transition-all duration-200 hover:opacity-90 hover:shadow-lg"
              >
                {ctaLink.label || 'Visit Website'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from hiding behind fixed header + banner */}
      <div style={{ height: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }} />
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'custom_ppc_tracking_phone_number',
      suggestedFields: ['custom_ppc_tracking_phone_number', 'property_call_tracking_phone', 'property_phone'],
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Header Logo',
      fields: {
        image: {
          label: 'Logo Image',
          type: 'image',
          // No defaultField — Header.tsx picks mono white vs mono black at
          // render time based on the primary color luminance.
          suggestedFields: [
            'property_mono_white_logo',
            'property_mono_black_logo',
            'property_primary_logo',
            'property_secondary_logo',
          ],
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultField: 'property_name',
        },
      },
    },
    'header.cta_link': {
      label: 'CTA Link',
      fields: {
        label: {
          label: 'CTA Text',
          type: 'text',
          defaultValue: 'Visit Website',
        },
        url: {
          label: 'CTA URL',
          type: 'url',
          defaultField: 'property_website_url',
          suggestedFields: ['property_website_url', 'property_apply_now_url'],
        },
        query_params: {
          label: 'URL Query Parameters',
          type: 'text',
          description: 'Query string appended to the URL, e.g. ?utm_medium=ppc&utm_source=google&utm_campaign=landing-page',
          defaultValue: '?utm_source=',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_blank',
        },
      },
    },
  },
};

export const designSchema: ComponentDesignSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'color.primary': {
      label: 'Background Color',
      type: 'color',
      description: 'Background color used by the fixed header.',
      cssVariable: '--vibe-color-primary',
    },
    'color.secondary': {
      label: 'CTA Background Color',
      type: 'color',
      description: 'Background color used by the CTA button.',
      cssVariable: '--vibe-color-secondary',
    },
    'color.textOnPrimary': {
      label: 'Header Text Color',
      type: 'color',
      description: 'Text color used on the primary header background.',
      cssVariable: '--vibe-color-text-on-primary',
    },
    'color.textOnSecondary': {
      label: 'CTA Text Color',
      type: 'color',
      description: 'Text color used on the CTA button.',
      cssVariable: '--vibe-color-text-on-secondary',
    },
    'font.heading': {
      label: 'Logo Font',
      type: 'font',
      description: 'Font family used when the logo falls back to property name text.',
      cssVariable: '--vibe-font-heading',
    },
    'font.body': {
      label: 'CTA Font',
      type: 'font',
      description: 'Font family used by the CTA button.',
      cssVariable: '--vibe-font-body',
    },
    'font.accent': {
      label: 'Phone Font',
      type: 'font',
      description: 'Font family used for the prominent phone number.',
      cssVariable: '--vibe-font-accent',
    },
    'shadow.md': {
      label: 'Header Shadow',
      type: 'shadow',
      description: 'Shadow applied below the fixed header.',
      cssVariable: '--vibe-shadow-md',
    },
    'radius.md': {
      label: 'CTA Radius',
      type: 'radius',
      description: 'Corner radius used by the CTA button.',
      cssVariable: '--vibe-radius-md',
    },
  },
};

export const requirements = defineComponentRequirements('Header', {
  designTokens: [
    'color.primary',
    'color.secondary',
    'color.textOnPrimary',
    'color.textOnSecondary',
    'font.heading',
    'font.body',
    'font.accent',
    'shadow.md',
    'radius.md',
  ],
  dataTokens: [
    branding.required('property_name', 'Property name displayed in header'),
    branding.optional('property_primary_logo', 'Primary logo (fallback when mono variants unset)'),
    branding.optional('property_mono_white_logo', 'Mono white logo (used on dark/medium primary backgrounds)'),
    branding.optional('property_mono_black_logo', 'Mono black logo (used on light primary backgrounds)'),
    contact.optional('property_call_tracking_phone', 'PPC call tracking number (preferred for headline phone)'),
    contact.optional('property_phone', 'Fallback phone number when call tracking is unset'),
    urls.optional('property_website_url', 'Website URL for CTA button'),
  ],
});

(Header as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;
(Header as unknown as { designSchema: ComponentDesignSchema }).designSchema = designSchema;
(Header as unknown as { requirements: typeof requirements }).requirements = requirements;

export default Header;
